import { useState } from 'react';
import { requestEmailChange } from 'src/services/client';
import { isApiError } from 'src/services/client';
import { ChangeEmailViewModel } from './changeEmailViewModel';

export type ChangeEmailStep = 'form' | 'submitting' | 'success';

const isValidEmailAddress = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const GENERIC_ERROR: string = 'Something went wrong. Please try again.';

export const useChangeEmailDialogue = (
  currentEmail: string
): ChangeEmailViewModel => {
  const [step, setStep] = useState<ChangeEmailStep>('form');
  const [newEmail, setNewEmailRaw] = useState('');
  const [confirmEmail, setConfirmEmailRaw] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const setNewEmail = (value: string) => {
    setNewEmailRaw(value.trim());
    setFieldError(null);
  };

  const setConfirmEmail = (value: string) => {
    setConfirmEmailRaw(value.trim());
    setConfirmError(null);
  };

  const validate = (currentEmail: string): boolean => {
    if (!newEmail.trim() || !isValidEmailAddress(newEmail)) {
      setFieldError('Please enter a valid email address.');
      return false;
    }

    if (newEmail.trim().toLowerCase() === currentEmail.trim().toLowerCase()) {
      setFieldError('New email must be different from your current email.');
      return false;
    }

    if (newEmail.trim() !== confirmEmail.trim()) {
      setConfirmError('Email addresses do not match.');
      return false;
    }

    return true;
  };

  const submitEmailChange = async (currentEmail: string): Promise<void> => {
    if (!validate(currentEmail)) {
      return;
    }

    setStep('submitting');

    const trimmedEmail: string = newEmail.trim();

    const response: any = await requestEmailChange(trimmedEmail);

    if (isApiError(response)) {
      setStep('form');
      setFieldError(GENERIC_ERROR);
      return;
    }

    if (!response.ok) {
      setStep('form');
      setFieldError(response.message ?? GENERIC_ERROR);
      return;
    }

    setStep('success');
  };

  const reset = () => {
    setStep('form');
    setNewEmailRaw('');
    setConfirmEmailRaw('');
    setFieldError(null);
    setConfirmError(null);
  };

  const emailsSameAsCurrent: boolean =
    newEmail.trim().length > 0 &&
    newEmail.trim().toLowerCase() === currentEmail.trim().toLowerCase();

  const emailsMismatch: boolean =
    confirmEmail.length > 0 && newEmail.trim() !== confirmEmail.trim();

  const newEmailError: string | null = emailsSameAsCurrent
    ? 'New email must be different from your current email.'
    : fieldError;

  const confirmFieldError: string | null = emailsMismatch
    ? 'Email addresses do not match.'
    : confirmError;

  const canSubmit: boolean =
    newEmail.trim().length > 0 &&
    confirmEmail.trim().length > 0 &&
    !emailsMismatch &&
    !emailsSameAsCurrent;

  return {
    currentEmail,
    newEmail,
    setNewEmail,
    confirm: {
      value: confirmEmail,
      onChange: setConfirmEmail,
      error: confirmFieldError,
    },
    error: newEmailError,
    isSubmitting: step === 'submitting',
    isSuccess: step === 'success',
    canSubmit,
    submit: () => {
      void submitEmailChange(currentEmail);
    },
    reset,
  };
};
